import { RecallAiMeetingsApi } from '../../api/recallaiMeetings'
import { formatDateTime } from '../../lib/utils'
import type { CalendarEvent, Meeting, MeetingEvent } from '../../types/MeetingDashboard'
import Spinner from '../Spinner'
import MeetingStatusBadge from './meeting-status-badge'
import { Modal, Timeline, Card } from 'flowbite-react'
import {
    Calendar,
    Clock,
    User,
    Video,
    LinkIcon,
    AlertTriangle,
    CheckCircle,
    XCircle,
    RefreshCw,
} from 'lucide-react'
import { useEffect, useState } from 'react'

;('use client')

interface MeetingDetailsModalProps {
    meeting?: Meeting | null
    isOpen: boolean
    onClose: () => void
    calendarEvent?: CalendarEvent | null
}

export default function MeetingDetailsModal({
    meeting,
    isOpen,
    onClose,
    calendarEvent,
}: MeetingDetailsModalProps) {
    const [meetingDetails, setMeetingDetails] = useState<Meeting | null>(null)
    const [meetingEvents, setMeetingEvents] = useState<MeetingEvent[]>([])
    const [isLoading, setIsLoading] = useState(false)

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'calendar_received':
                return <Calendar className="h-5 w-5 text-blue-600" />
            case 'backend_received':
                return <CheckCircle className="h-5 w-5 text-green-600" />
            case 'ready':
                return <Calendar className="h-5 w-5 text-green-600" />
            case 'joining_call':
                return <RefreshCw className="h-5 w-5 text-blue-600" />
            case 'in_waiting_room':
                return <Clock className="h-5 w-5 text-yellow-600" />
            case 'in_call_recording':
                return <Video className="h-5 w-5 text-green-600" />
            case 'in_call_not_recording':
                return <AlertTriangle className="h-5 w-5 text-gray-600" />
            case 'recording_permission_not_allowed':
                return <AlertTriangle className="h-5 w-5 text-gray-600" />
            case 'recording_permission_allowed':
                return <CheckCircle className="h-5 w-5 text-gray-600" />
            case 'call_ended':
                return <CheckCircle className="h-5 w-5 text-gray-600" />
            case 'recording_done':
                return <CheckCircle className="h-5 w-5 text-gray-600" />
            case 'done':
                return <CheckCircle className="h-5 w-5 text-gray-600" />
            case 'fatal':
                return <XCircle className="h-5 w-5 text-red-600" />
            case 'error':
                return <XCircle className="h-5 w-5 text-red-600" />
            default:
                return <AlertTriangle className="h-5 w-5 text-gray-600" />
        }
    }

    const getTimelineItems = () => {
        const items: any[] = []

        if (meetingEvents && meetingEvents.length > 0) {
            return meetingEvents.map((event, index) => (
                <Timeline.Item key={index}>
                    <Timeline.Point icon={() => getStatusIcon(event.title)} />
                    <Timeline.Content>
                        <Timeline.Time>{formatDateTime(event.created_at)}</Timeline.Time>
                        <Timeline.Title>{event.title}</Timeline.Title>
                        {event.description && <Timeline.Body>{event.description}</Timeline.Body>}
                        {event.sub_code && <Timeline.Body>{event.sub_code}</Timeline.Body>}
                    </Timeline.Content>
                </Timeline.Item>
            ))
        }

        return items
    }

    useEffect(() => {
        if (isOpen) {
            setIsLoading(true)
            if (meeting) {
                setMeetingDetails(meeting)
                // This would trigger an API call to fetch meeting details
                RecallAiMeetingsApi.getMeetingEventsForMeetingId(meeting.id)
                    .then((res) => {
                        // eslint-disable-next-line no-console
                        //console.log('Meeting events:', res.data.meeting_events)
                        setMeetingEvents(res.data.meeting_events)
                    })
                    .catch((error) => {
                        // eslint-disable-next-line no-console
                        console.error('Error fetching meeting events:', error)
                    })
            } else if (calendarEvent) {
                // Create a new meeting object from the calendar event
                const newMeeting: Meeting = {
                    id: calendarEvent.event_id,
                    calendarEventId: calendarEvent.event_id,
                    title: calendarEvent.title,
                    host: {
                        id: '',
                        name: calendarEvent.host,
                        email: '',
                        avatar: '',
                    },
                    meeting_type: calendarEvent.meeting_type,
                    start: calendarEvent.start,
                    end: calendarEvent.end,
                    status: 'calendar_received',
                    meeting_url: calendarEvent.meeting_url,
                    backendRecord: calendarEvent.backendRecord || false,
                    calendarSynced: true,
                    events: [],
                }
                setMeetingDetails(newMeeting)
                setMeetingEvents([])
            }
            setIsLoading(false)
        }
    }, [isOpen])

    return (
        <Modal show={isOpen} onClose={onClose} size="4xl">
            <Modal.Header>Meeting Details</Modal.Header>
            <Modal.Body>
                {isLoading ? (
                    <>
                        <Spinner />
                    </>
                ) : (
                    <div className="space-y-6">
                        <div className="flex justify-between items-start">
                            <div>
                                <h3 className="text-xl font-bold">{meetingDetails?.title}</h3>
                                <div className="flex items-center mt-2">
                                    <User className="w-4 h-4 mr-1 text-gray-500" />
                                    <span className="text-gray-600 mr-4">
                                        {meetingDetails?.host.name}
                                    </span>

                                    <Video className="w-4 h-4 mr-1 text-gray-500" />
                                    <span className="text-gray-600">
                                        {meetingDetails?.meeting_type}
                                    </span>
                                </div>
                            </div>
                            <MeetingStatusBadge status={meetingDetails?.status || ''} />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Card>
                                <h5 className="text-lg font-bold tracking-tight text-gray-900 mb-2">
                                    Meeting Information
                                </h5>
                                <div className="space-y-2">
                                    <div className="flex items-center">
                                        <Calendar className="w-4 h-4 mr-2 text-gray-500" />
                                        <span className="text-sm text-gray-700">
                                            Start: {formatDateTime(meetingDetails?.start || '')}
                                        </span>
                                    </div>
                                    {meetingDetails?.end && (
                                        <div className="flex items-center">
                                            <Clock className="w-4 h-4 mr-2 text-gray-500" />
                                            <span className="text-sm text-gray-700">
                                                End: {formatDateTime(meetingDetails?.end || '')}
                                            </span>
                                        </div>
                                    )}
                                    <div className="flex items-center">
                                        <LinkIcon className="w-4 h-4 mr-2 text-gray-500" />
                                        <div className="pr-1 overflow-x-auto">
                                            {meetingDetails?.meeting_url}
                                        </div>
                                    </div>
                                </div>
                            </Card>

                            <Card>
                                <h5 className="text-lg font-bold tracking-tight text-gray-900 mb-2">
                                    System Information
                                </h5>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Meeting ID:</span>
                                        <span className="font-mono">{meetingDetails?.id}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Calendar Sync:</span>
                                        <span
                                            className={
                                                meetingDetails?.calendarSynced
                                                    ? 'text-green-600'
                                                    : 'text-red-600'
                                            }
                                        >
                                            {meetingDetails?.calendarSynced
                                                ? 'Synced'
                                                : 'Not Synced'}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Backend Record:</span>
                                        <span
                                            className={
                                                meetingDetails?.backendRecord
                                                    ? 'text-green-600'
                                                    : 'text-red-600'
                                            }
                                        >
                                            {meetingDetails?.backendRecord ? 'Created' : 'Missing'}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Recall AI Status:</span>
                                        <span
                                            className={
                                                meetingDetails?.recallStatus === 'success'
                                                    ? 'text-green-600'
                                                    : meetingDetails?.recallStatus === 'pending'
                                                    ? 'text-yellow-600'
                                                    : 'text-red-600'
                                            }
                                        >
                                            {meetingDetails?.recallStatus || 'Unknown'}
                                        </span>
                                    </div>
                                </div>
                            </Card>
                        </div>

                        <div>
                            <h5 className="text-lg font-bold mb-4 max-h-[20%] overflow-auto">
                                Meeting Timeline
                            </h5>
                            <Timeline>{getTimelineItems()}</Timeline>
                        </div>

                        {/* {(meetingDetails?.status === 'failed' || meetingDetails?.status === 'waiting_room') && (
                        <div className="flex justify-end">
                            <Button color="blue" onClick={handleRetryJoin}>
                                <RefreshCw className="mr-2 h-5 w-5" />
                                Retry Join
                            </Button>
                        </div>
                    )} */}
                    </div>
                )}
            </Modal.Body>
        </Modal>
    )
}
